import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
  IconButton,
  Typography,
  Grid,
  Paper,
  InputAdornment,
  Button,
  Tabs,
  Tab,
  Box
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import LambdaFetch from '../functions/FetchFromLambda'
import LoadingButton from '../components/LoadingButton'
import TableMui from '../components/TableMui'
import getTableOptions from '../constants/TableOptions'
import DialogTable from '../components/DialogTable'
import LoadingCircle from '../components/common/LoadingCircle'
import { smallScreenWidth } from '../constants/AppConstants'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import { DashboardAthenaTableServerSideNoSelect } from '../components/DashboardAthenaTableServerSideNoSelect'
import AutocompleteDropDown from '../components/AutocompleteDropdown'
import ServerSideAutoCompletev2 from '../functions/ServerSideAutoCompletev2'
import TextField from '@material-ui/core/TextField'
import {
  employeeAbsencePlanDatesCols,
  employeeAbsencePlanBalancesCols,
  employeeAbsencePlanCarryoverCols,
  employeeAbsencePlanGLAccountsCols,
  employeeAbsencePlanOverrideTablesCols,
  employeeAbsencePlanPayrollCols,
  useEmployeeAbsenceTransactionCols
} from '../constants/AbsenceManagementCols'
import DashboardAthenaTableClientSide from "../components/DashboardAthenaTableClientSide";
import {glChartReactTableCols} from "../constants/GlCols";
import { AntTabs } from '../components/VendorTabs'
import { debounce } from 'lodash'
import AbsenceManagementMain from '../components/AbsenceManagementMain'
import AthenaParameterTransformation from "../utils/AthenaParameterTransformation";
const _ = require('lodash')

const serversideEntriesPerPage = 50

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    margin: '2rem 1rem'
  },
  treeRoot: {
    flexGrow: 1,
    margin: '1rem'
  }
}))

const defaultHeaders = {
  year: '',
  department: '',
  employeeId: '',
  absenceType: '',
  status: '',
  fromDate: '',
  toDate: '',
  includeInactive: 0
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`absence-tabpanel-${index}`}
      aria-labelledby={`absence-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `absence-tab-${index}`,
    'aria-controls': `absence-tabpanel-${index}`,
  };
}

export default function AbsenceManagement(props) {
  const [state, setState] = useState({
    options: null,
    isLoading: false,
    data: null,
    fetched: true,
    isServerSide: false,
    tableKey: JSON.stringify(defaultHeaders)
  })
  const [showTable, setShowTable] = useState(false)
  const [headers, setHeaders] = useState(defaultHeaders)
  const [detailsInfo, setDetailsInfo] = useState(null)
  const [clearHeader, setClearHeaders] = useState(false)
  const [tabValue, setTabValue] = useState(0)
  const [tableRefreshKey, setTableRefreshKey] = useState(0)
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const [absenceData, setAbsenceData] = useState({})
  const [isLoadingMainData, setIsLoadingMainData] = useState(false)
  const classes = useStyles()
  
  const transactionCols = useEmployeeAbsenceTransactionCols()

  const { credentials } = props.fetchInitialData

  const handleClose = () => {
    setDetailsInfo(null)
  }

  // Create a debounced submit handler
  const debouncedSubmit = React.useCallback(
    debounce((e) => {
      setShowTable(true)
      setState(prevState => ({ ...prevState, tableKey: JSON.stringify(headers) }))
      setTableRefreshKey(prevKey => prevKey + 1)
      
      // Re-enable the button after 2 seconds
      setTimeout(() => {
        setIsSubmitDisabled(false)
      }, 2000)
    }, 300),
    [headers]
  )

  const handleSubmit = e => {
    e.preventDefault()
    if (!isSubmitDisabled) {
      setIsSubmitDisabled(true)
      setState(prevState => ({ ...prevState, isLoading: true }))
      debouncedSubmit(e)
      
      // Set loading state back to false after a short delay
      setTimeout(() => {
        setState(prevState => ({ ...prevState, isLoading: false }))
      }, 1000)
    }
  }

  const handleCallback = (values, error) => {
    if (error) {
      setState({ ...state, isLoading: false })
      setShowTable(false)
      return null
    }
    setState({ ...state, isLoading: false })
  }

  const viewDetails = async absenceId => {
    const resp = await LambdaFetch(
      'absence-management',
      'post',
      credentials.user.accessToken,
      JSON.stringify({
        action: 'get-absence-details',
        absenceId
      }),
      '',
      credentials
    )

    setDetailsInfo(resp.data.detailsData)
  }

  const tableDataTransformation = rows => {
    return rows
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Function to fetch data for the Main tab
  const fetchMainData = async () => {
    try {
      setIsLoadingMainData(true)
      const params = AthenaParameterTransformation('employee_absence_plan', headers)
      const fetchBody = {
        dashboard_name: 'employee_absence_plan',
        ...params,
        pageSize: 1, // We only need one record for the main view
        roles: 'app_user,all-access,default',
        frontendTable: true
      }
      
      const resp = await LambdaFetch(
        'dashboard-trigger-temp-table',
        'post',
        props.fetchInitialData.credentials.user.accessToken,
        JSON.stringify(fetchBody),
        '',
        credentials
      )
      
      if (resp.data && resp.data.response && resp.data.response.data && resp.data.response.data.length > 0) {
        setAbsenceData(resp.data.response.data[0])
      } else {
        setAbsenceData({})
      }
      setIsLoadingMainData(false)
    } catch (error) {
      console.error("Error fetching main data:", error)
      setAbsenceData({})
      setIsLoadingMainData(false)
    }
  }

  // Update useEffect to fetch data when the component mounts or when headers change
  useEffect(() => {
    if (showTable) {
      fetchMainData()
    }
  }, [showTable, tableRefreshKey])

  if (!state.fetched) {
    return <LoadingCircle />
  }

  const options = getTableOptions({
    cols: employeeAbsencePlanDatesCols,
    fileName: `Absence_Management`
  })
  options.downloadOptions.filterOptions.useDisplayedColumnsOnly = false

  return (
    <div style={{ padding: '1.5rem 0.5rem' }}>
      <DialogTable
        isOpen={!!detailsInfo}
        handleClose={handleClose}
        tableData={detailsInfo}
        cols={employeeAbsencePlanDatesCols}
        simpleTable={true}
      />
      
      <IconButton
        style={{ borderRadius: '5px', position: 'absolute' }}
        size='small'
        onClick={() => props.history.push('/')}
        aria-label='delete'
      >
        <ArrowBackIcon />
        {props.fetchInitialData.credentials.appWidth > smallScreenWidth &&
          'Dashboard'}
      </IconButton>
      
      <Typography variant='h5' style={{ textAlign: 'center' }}>
        Absence Management
      </Typography>
      
      <div style={{ margin: '1rem' }}>
        <Paper style={{ padding: '1rem' }}>
          <form
            onSubmit={e => {
              e.preventDefault()
              handleSubmit(e)
            }}
          >
            <Grid
              container
              spacing={2}
              direction='row'
              justifyContent='flex-start'
              alignItems='center'
            >
              {[
                {
                  id: 'company',
                  dataField: 'COMPANY',
                  label: 'Company',
                  dateType: 'string',
                  isRequired: true
                },
                {
                  id: 'employee',
                  dataField: 'EMPLOYEE',
                  label: 'Employee',
                  dateType: 'string',
                  isRequired: true
                },
                {
                  id: 'plan',
                  dataField: 'PLAN',
                  label: 'Plan',
                  dateType: 'string',
                  isRequired: true
                },
                {
                  id: 'structureGroup',
                  dataField: 'EMPLOYEE_GROUP',
                  label: 'Employee Group',
                  dateType: 'string',
                  isRequired: true
                }
              ].map(field => {
                return (
                    <Grid key={field.id} item>
                      <ServerSideAutoCompletev2
                          required={field.isRequired}
                          id={field.id}
                          width='250px'
                          label={field.label}
                          key={clearHeader}
                          dontAutoHighlight
                          dontOpenOnFocus
                          accessToken={credentials.user.accessToken}
                          error={`No ${field.label} Found`}
                          variant='standard'
                          handleChange={(id, hit) => {
                            setHeaders({ ...headers, [field.id]: id })
                          }}
                          credentials={credentials}
                          apiResource='employee-form'
                          secondaryParams={{
                            limit: 10,
                            lookupField: field.dataField,
                            action: 'autocomplete_absence_management',
                            ...headers
                          }}
                      />
                    </Grid>
                )
              })}
            </Grid>

            <div style={{ marginTop: '1rem' }}>
              <LoadingButton
                label='Submit'
                isLoading={state.isLoading}
                color='primaryVLButton'
                buttonType='submit'
                disabled={isSubmitDisabled}
              />
              {!!(
                headers.employee ||
                headers.company ||
                headers.lookup ||
                headers.structureGroup
              ) && (
                <Button
                  onClick={() => {
                    setClearHeaders(!clearHeader)
                    setHeaders(defaultHeaders)
                  }}
                  style={{ marginLeft: '0.5rem' }}
                >
                  {' '}
                  Clear
                </Button>
              )}
            </div>
          </form>
        </Paper>

        {showTable && (
          <div style={{ marginTop: '1rem' }}>
            {/* Plan Information Summary */}
            <Paper style={{ padding: '1rem', marginBottom: '1rem' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="textSecondary">Company:</Typography>
                  <Typography variant="body1">{headers.company || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="textSecondary">Employee:</Typography>
                  <Typography variant="body1">{headers.employee || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="textSecondary">Plan:</Typography>
                  <Typography variant="body1">{headers.plan || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="textSecondary">Employee Group:</Typography>
                  <Typography variant="body1">{headers.structureGroup || 'N/A'}</Typography>
                </Grid>
              </Grid>
            </Paper>
            
            <Paper>
              <div style={{ padding: '1rem' }}>
                <AntTabs
                  value={tabValue}
                  handleChange={handleTabChange}
                  color="#1976d2"
                  tabs={[
                    "Main",
                    "Transactions"
                  ]}
                />
              </div>
              
              <TabPanel value={tabValue} index={0}>
                <AbsenceManagementMain data={absenceData} isLoading={isLoadingMainData} />
              </TabPanel>
              
              <TabPanel value={tabValue} index={1}>
                <DashboardAthenaTableClientSide
                  key={`transactions-${tableRefreshKey}`}
                  {...props}
                  credentials={props.fetchInitialData.credentials}
                  cols={transactionCols}
                  queryName={'employee_absence_transaction'}
                  headers={{ ...headers }}
                />
              </TabPanel>
            </Paper>
          </div>
        )}
      </div>
    </div>
  )
}
